import { useMemo } from "react";
import type { Document } from "@/types/documents";

interface UseTagFilteringProps {
  documents: Document[];
  activeFilters: string[];
}

interface FilteredResult {
  filteredDocuments: Document[];
  matchingCount: number;
  totalCount: number;
}

export function useTagFiltering({
  documents,
  activeFilters,
}: UseTagFilteringProps): FilteredResult {
  const filteredDocuments = useMemo(() => {
    if (activeFilters.length === 0) {
      return documents;
    }

    // Group filters by type for AND logic across groups, OR within groups
    const filterGroups = {
      project: activeFilters.filter((f) => f.startsWith("project-")),
      category: activeFilters.filter((f) => f.startsWith("category-")),
      discipline: activeFilters.filter((f) => f.startsWith("discipline-")),
      custom: activeFilters.filter((f) => f.startsWith("custom-")),
      construction: activeFilters.filter((f) => f.startsWith("construction-")),
      stage: activeFilters.filter((f) => f.startsWith("stage-")),
      untagged: activeFilters.filter((f) => f === "untagged"),
      all: activeFilters.filter((f) => f === "all"),
    };

    return documents.filter((doc) => {
      // If 'all' is selected, include all documents
      if (filterGroups.all.length > 0) return true;

      // Check each group - document must match at least one filter in each active group
      const groupResults = [];

      // Project group - OR within group
      if (filterGroups.project.length > 0) {
        const projectMatch = filterGroups.project.some((filterId) => {
          const projectId = filterId.replace("project-", "");
          return (
            doc.project_id?.toString() === projectId ||
            (doc.project_name && filterId.includes(doc.project_name))
          );
        });
        groupResults.push(projectMatch);
      }

      // Category group - OR within group
      // Match directly against doc.categories[].name (M2M relation)
      if (filterGroups.category.length > 0) {
        const categoryMatch = filterGroups.category.some((filterId) => {
          const categoryName = filterId.replace("category-", "");
          return (
            Array.isArray(doc.categories) &&
            doc.categories.some((cat) => cat.name.toLowerCase() === categoryName.toLowerCase())
          );
        });
        groupResults.push(categoryMatch);
      }

      // Discipline group - OR within group
      if (filterGroups.discipline.length > 0) {
        const disciplineMatch = filterGroups.discipline.some((filterId) => {
          const code = filterId.replace("discipline-", "");
          return Array.isArray(doc.project_disciplines) && doc.project_disciplines.includes(code);
        });
        groupResults.push(disciplineMatch);
      }

      // Custom group - OR within group
      // Match directly against doc.categories[].name (M2M relation)
      if (filterGroups.custom.length > 0) {
        const customMatch = filterGroups.custom.some((filterId) => {
          const customTag = filterId.replace("custom-", "");
          return (
            Array.isArray(doc.categories) &&
            doc.categories.some((cat) => cat.name.toLowerCase() === customTag.toLowerCase())
          );
        });
        groupResults.push(customMatch);
      }

      // Design disciplines group - OR within group
      if (filterGroups.construction.length > 0) {
        const constructionMatch = filterGroups.construction.some((filterId) => {
          const disciplineId = filterId.replace("construction-", "");
          return (
            Array.isArray(doc.design_disciplines) &&
            doc.design_disciplines.some(
              (d) => String(d.id) === disciplineId || d.name === disciplineId
            )
          );
        });
        groupResults.push(constructionMatch);
      }

      // Design stages group - OR within group
      if (filterGroups.stage.length > 0) {
        const stageMatch = filterGroups.stage.some((filterId) => {
          const stageId = filterId.replace("stage-", "");
          return (
            Array.isArray(doc.design_stages) &&
            doc.design_stages.some((s) => String(s.id) === stageId || s.name === stageId)
          );
        });
        groupResults.push(stageMatch);
      }

      // Untagged group
      if (filterGroups.untagged.length > 0) {
        const hasCategories = Array.isArray(doc.categories) && doc.categories.length > 0;
        const hasDisciplines =
          Array.isArray(doc.design_disciplines) && doc.design_disciplines.length > 0;
        const hasStages = Array.isArray(doc.design_stages) && doc.design_stages.length > 0;
        // Document is untagged if it has no project, no categories, no disciplines, and no stages
        const untaggedMatch = !doc.project_name && !hasCategories && !hasDisciplines && !hasStages;
        groupResults.push(untaggedMatch);
      }

      // Document must match ALL active groups (AND logic across groups)
      return groupResults.length > 0 && groupResults.every((result) => result === true);
    });
  }, [documents, activeFilters]);

  return {
    filteredDocuments,
    matchingCount: filteredDocuments.length,
    totalCount: documents.length,
  };
}

// Alternative hook for AND logic (if needed in the future)
export function useTagFilteringAnd({
  documents,
  activeFilters,
}: UseTagFilteringProps): FilteredResult {
  const filteredDocuments = useMemo(() => {
    if (activeFilters.length === 0) {
      return documents;
    }

    return documents.filter((doc) => {
      // Check if document matches ALL of the active filters (AND logic)
      return activeFilters.every((filterId) => {
        if (filterId === "all") return true;

        if (filterId === "untagged") {
          const hasCategories = Array.isArray(doc.categories) && doc.categories.length > 0;
          const hasDisciplines =
            Array.isArray(doc.design_disciplines) && doc.design_disciplines.length > 0;
          const hasStages = Array.isArray(doc.design_stages) && doc.design_stages.length > 0;
          return !doc.project_name && !hasCategories && !hasDisciplines && !hasStages;
        }

        if (filterId.startsWith("project-")) {
          const projectId = filterId.replace("project-", "");
          return doc.project_id?.toString() === projectId;
        }

        if (filterId.startsWith("category-")) {
          const categoryName = filterId.replace("category-", "");
          return (
            Array.isArray(doc.categories) &&
            doc.categories.some((cat) => cat.name.toLowerCase() === categoryName.toLowerCase())
          );
        }

        if (filterId.startsWith("custom-")) {
          const customTag = filterId.replace("custom-", "");
          return (
            Array.isArray(doc.categories) &&
            doc.categories.some((cat) => cat.name.toLowerCase() === customTag.toLowerCase())
          );
        }

        if (filterId.startsWith("discipline-")) {
          const code = filterId.replace("discipline-", "");
          return Array.isArray(doc.project_disciplines) && doc.project_disciplines.includes(code);
        }

        if (filterId.startsWith("construction-")) {
          const disciplineId = filterId.replace("construction-", "");
          return (
            Array.isArray(doc.design_disciplines) &&
            doc.design_disciplines.some(
              (d) => String(d.id) === disciplineId || d.name === disciplineId
            )
          );
        }

        if (filterId.startsWith("stage-")) {
          const stageId = filterId.replace("stage-", "");
          return (
            Array.isArray(doc.design_stages) &&
            doc.design_stages.some((s) => String(s.id) === stageId || s.name === stageId)
          );
        }

        return false;
      });
    });
  }, [documents, activeFilters]);

  return {
    filteredDocuments,
    matchingCount: filteredDocuments.length,
    totalCount: documents.length,
  };
}
